import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useRef, useState } from "react";
import { Button, Card, Col, Form, Row, Tab, Tabs } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";

import BreadCrumbTable from "components/breadcrumb/BreadCrumbTable";
import { useTranslation } from "react-i18next";
import { mapApiVariantsToUI, mapUIVariantsToApi } from "utils/functions";
import PageTitle from "utils/PageTitle";
import { ProductSchema } from "../ProductSchema";

// Import your tab components
import CustomizationPrice from "./customization-price/CustomizationPrice";
import Details from "./details/Details";
import ProductGeneralInfo from "./general-info/ProductGeneralInfo";
import MediaInfoV1 from "./media-info/MediaInfoV1";
import PricingAndStockInfoV3 from "./pricing-and-stock/PricingAndStockInfoV3";
import Promotions from "./promotions/Promotions";
import RelatedProducts from "./related-products/RelatedProducts";
import ShippingAndWarrantyInfo from "./shipping-and-warranty/ShippingAndWarrantyInfo";
import SocialPlatforms from "./social-platforms/SocialPlatforms";
import Specifications from "./specifications/Specifications";
import Variants from "./variants/Variants";
import { useEditData } from "../EditContext";

// Define fields associated with each tab
const TAB_FIELDS = {
  basicInfo: ["name", "categoryId", "type", "tags", "salesPrice", "cost"],
  mediaInfo: ["mediaInfos"],
  specification: ["specifications"],
  variants: ["productVariants"],
  pricingAndStock: ["pricingAndStocks"],
  promotions: ["productPromotions"],
  socialPlatforms: ["productSocialLinks"],
  detail: ["description", "highlights", "insideBox"],
  shippingAndWarranty: [
    "weight",
    "weightUOM",
    "length",
    "width",
    "height",
    "warranty",
    "warrantyType",
  ],
  relatedProducts: ["relatedProducts"],
  customizationPrice: ["customizationPrice"],
};

const AddProduct = () => {
  const state = useEditData();

  const { t } = useTranslation();
  const [key, setKey] = useState("basicInfo");
  const navigate = useNavigate();
  const location = useLocation();
  const errorTabIndexRef = useRef(0);

  // Initialize form
  const methods = useForm({
    resolver: yupResolver(ProductSchema),
    defaultValues: {
      name: "",
      categoryId: "",
      description: "",
      highlights: "",
      insideBox: "",
      type: "",
      tags: [],
      salesPrice: "",
      cost: "",
      status: true,
      mediaInfos: [],
      specifications: [],
      relatedProducts: [],
      productPromotions: [],
      productSocialLinks: [],
      weight: 0,
      weightUOM: "kg",
      length: 0,
      width: 0,
      height: 0,
      volumeUOM: "cm",
      warranty: "",
      warrantyType: "",
      warrantyPolicy: "",
      productVariants: location.state?.productVariants
        ? mapApiVariantsToUI(location.state.productVariants)
        : [],
      pricingAndStocks: [],
    },
    shouldUnregister: false,
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = methods;

  const customizationId = watch("customizationId");
  const isCustomizable = watch("isCustomizable");

  // Compute tabs with errors
  const errorTabs = React.useMemo(() => {
    return Object.entries(TAB_FIELDS)
      .filter(([_, fields]) => fields.some((field) => errors?.[field]))
      .map(([tabKey]) => tabKey);
  }, [errors]);

  // Reset error index if errorTabs length changes
  useEffect(() => {
    errorTabIndexRef.current = 0;
  }, [errorTabs.length]);

  // Move to first error tab on invalid submit
  const onInvalid = () => {
    if (!errorTabs.length) return;

    const index = errorTabIndexRef.current;
    setKey(errorTabs[index]);

    errorTabIndexRef.current = index + 1 < errorTabs.length ? index + 1 : 0;
  };

  // Map UI variants to API format
  const mapDataForApi = (data) => {
    let updatedSocialPlatforms = data.productSocialLinks.map((sc) => ({
      ...sc,
      socialPlatformId: sc.socialPlatformId || "0",
      url: sc.url || "",
    }));

    let updatedProductPromotions = data.productPromotions.map((p) => ({
      ...p,
      promotionId: p.promotionId || "0",
    }));

    let updatedRelatedProducts = data.relatedProducts.map((p) => ({
      ...p,
      relatedProductId: p.relatedProductId || "0",
    }));

    let updatedMediaInfos = data.mediaInfos.map((m) => ({
      ...m,
      contentType: m.contentType || "",
      mediaInfo: m.mediaInfo || "",
    }));

    let pricingAndStocks = data.pricingAndStocks.map((p) => ({
      ...p,
      originalPrice: p.originalPrice || 0,
      discountedPrice: p.discountedPrice || 0,
      skuCode: p.skuCode || "",
      quantity: p.quantity || 0,
    }));

    console.log("data.productVariants", data.productVariants);

    return {
      ...data,
      id: location?.state?.id || 0,
      tags: data.tags.join("|"),
      mediaInfos: updatedMediaInfos,
      relatedProducts: updatedRelatedProducts,
      pricingAndStocks,
      productVariants: Array.isArray(data.productVariants)
        ? mapUIVariantsToApi(data.productVariants)
        : [],
      productPromotions: updatedProductPromotions,
      productSocialLinks: updatedSocialPlatforms,
      volumeUOM: data.volumeUOM || "cm",
      weightUOM: data.weightUOM || "kg",
      customizationId: data.isCustomizable ? data.customizationId || 0 : 0,
    };
  };

  const onSubmit = async (data) => {
    console.log(data);

    const myData = mapDataForApi(data);

    console.log(myData);

    // try {
    //   await ProductsPostAPI.post(myData);
    //   toast.success(
    //     location?.state
    //       ? "Product updated successfully"
    //       : "Product created successfully",
    //     {
    //       position: customSettings.toastPosition,
    //       autoClose: customSettings.toastClose,
    //     },
    //   );

    //   reset(); // reset form
    //   navigate(-1);
    // } catch (error) {
    //   toast.error("Something went wrong", {
    //     position: customSettings.toastPosition,
    //     autoClose: customSettings.toastClose,
    //   });
    //   console.error(error);
    // }
  };

  // Load edit data
  useEffect(() => {
    if (state) {
      console.log("state", state);
      reset({
        ...state,
        productVariants: mapApiVariantsToUI(state.productVariants),
      });
    }
  }, [state, reset]);

  // Tab title with error indicator
  const tabTitle = (label, tabKey) => {
    const hasError = errorTabs.includes(tabKey);
    return (
      <span
        className={`d-flex align-items-center gap-1 ${
          hasError ? "text-danger fw-semibold" : ""
        }`}
      >
        {label}
        {/* {hasError && <span>⚠</span>} */}
      </span>
    );
  };

  return (
    <>
      <PageTitle name="Products" />
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(onSubmit, onInvalid)}>
          <Card>
            {/* <Card.Header className="border-bottom">
              <Row className="flex-between-center shadow-none">
                <Col md>
                  <BreadCrumbTable
                    title="Products"
                    subTitle={location?.state?.name}
                  />
                </Col>
                <Col xs="auto">
                  <Button
                    variant="link"
                    className="text-secondary fw-medium p-0 me-3"
                    type="button"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit">
                    Ok
                  </Button>
                </Col>
              </Row>
            </Card.Header> */}

            <div
              // className="py-2 mb-3"
              style={{
                height: `calc(100vh - 8.4rem)`,
                overflow: "auto",
              }}
            >
              <Tabs activeKey={key} onSelect={setKey} unmountOnExit={false}>
                <Tab
                  eventKey="basicInfo"
                  title={tabTitle(t("general"), "basicInfo")}
                  className="p-3"
                >
                  <ProductGeneralInfo />
                </Tab>
                <Tab
                  eventKey="mediaInfo"
                  title={tabTitle(t("gallery"), "mediaInfo")}
                  className="p-3"
                >
                  <MediaInfoV1 />
                </Tab>
                <Tab
                  eventKey="specification"
                  title={tabTitle(t("specification"), "specification")}
                  className="p-3"
                >
                  <Specifications />
                </Tab>
                <Tab
                  eventKey="variants"
                  title={tabTitle(t("variants"), "variants")}
                  className="p-3"
                >
                  <Variants />
                </Tab>
                <Tab
                  eventKey="pricingAndStock"
                  title={tabTitle(t("pricingAndStock"), "pricingAndStock")}
                  className="p-3"
                >
                  <PricingAndStockInfoV3 />
                </Tab>
                <Tab
                  eventKey="promotions"
                  title={tabTitle(t("promotions"), "promotions")}
                  className="p-3"
                >
                  <Promotions />
                </Tab>
                <Tab
                  eventKey="socialPlatforms"
                  title={tabTitle(t("socialPlatforms"), "socialPlatforms")}
                  className="p-3"
                >
                  <SocialPlatforms />
                </Tab>
                <Tab
                  eventKey="detail"
                  title={tabTitle(t("details"), "detail")}
                  className="p-3"
                >
                  <Details />
                </Tab>
                <Tab
                  eventKey="shippingAndWarranty"
                  title={tabTitle(
                    t("shippingAndWarranty"),
                    "shippingAndWarranty",
                  )}
                  className="p-3"
                >
                  <ShippingAndWarrantyInfo />
                </Tab>
                <Tab
                  eventKey="relatedProducts"
                  title={tabTitle(t("relatedProduct"), "relatedProducts")}
                  className="p-3"
                >
                  <RelatedProducts />
                </Tab>
                <Tab
                  eventKey="customizationPrice"
                  title={tabTitle(
                    t("customizationPrice"),
                    "customizationPrice",
                  )}
                  className="p-3"
                  tabClassName={
                    customizationId && isCustomizable ? "" : "d-none"
                  }
                >
                  <CustomizationPrice />
                </Tab>
              </Tabs>
            </div>
          </Card>
        </Form>
      </FormProvider>
    </>
  );
};

export default AddProduct;
